import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { resolvePlayerMark, useBlipFeed } from '../hooks/useBlipFeed.js';
import { useMissionList } from '../hooks/useMissionList.js';
import { MissionRows } from './MissionList.jsx';
import { useFreeze } from './freezeOwner.js';

/**
 * @file THE PHONE: the device the player carries, as a modal with two screens —
 * the job list, and an order for a box of ammunition.
 *
 * Built on `ShopPanel.jsx`'s BEHAVIOUR, deliberately: the same scrim, the same
 * header/body/footer, the same row shape, the same key ladder. A player who has
 * stood at a till has already learned this screen. Read that file's header first
 * — every trap it documents applies here, and two of them are repeated below
 * only because they are the ones a reader is most likely to "fix".
 *
 * ⚠ THE ONE PLACE IT DELIBERATELY LEAVES THAT MOULD IS ITS SHAPE. It shipped as
 * the till's 38 rem landscape card, and a phone that draws as a wide menu is a
 * menu — the fiction is that the player took a handset out of their pocket, and
 * every other beat of this feature (the ring, the call, the two apps) is written
 * to that fiction. So the dialog root is a BEZEL, the card is its SCREEN, and
 * the whole thing is PORTRAIT and narrow. Nothing about the model, the bus
 * contract, the freeze or the keyboard moved with it — this is pixels only.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS A PURE RENDERER
 * ─────────────────────────────────────────────────────────────────────────────
 * Nothing here decides anything about a phone. `state` is the only thing that
 * enables a row, `reason` is the only thing that explains one, and `status` is
 * printed VERBATIM — this file does not know what `'INBOUND'` means and must
 * never learn, because the day it grows a colour table keyed on that string is
 * the day the model can add a sixth status and have the HUD print it in the
 * wrong voice. There is no local copy of ammunition, no cooldown clock, no
 * "already ordered" flag, no arithmetic on anything the model owns. The screen
 * changes because a snapshot arrived.
 *
 * ⚠ THE VIEW LIVES IN THE MODEL TOO, and that is the part that looks like
 * over-engineering and is not. A `useState('missions')` here would be a second
 * authority on "which screen is up": the review scene could not drive it over
 * the bus, `stats().phone.view` could not be a real debug readout, and a model
 * that wanted to open the phone ON the order screen (an inbound delivery, a
 * callback) would have no way to say so. So the view buttons emit
 * `'phone:view'` and the screen changes when `'phone:state'` comes back. The
 * round trip is SYNCHRONOUS and the loop is frozen underneath it, so it costs
 * one function call and buys a single source of truth.
 *
 * ⚠ AND THE TWO VIEW BUTTONS ARE NOT THE "NO TABS" RULE BEING BROKEN.
 * `ShopPanel.jsx` refuses a tab strip, and that refusal is about hiding rows of
 * ONE list behind a control the player has to discover — a shop is one
 * catalogue and the player is in it for four seconds. These are not tabs over
 * one list: they are two SCREENS OF A DEVICE, and a phone genuinely has apps.
 * The missions app and the order app share no rows, no ordering and no verbs.
 * Do not "unify" them into one scrolling list.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BUS CONTRACT
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 * engine → ui   'phone:open'   { token, view, status, rows, calls }
 * engine → ui   'phone:state'  the same payload, republished after every change
 * engine → ui   'phone:close'  { token }              ⚠ two-way
 * ui → engine   'phone:view'   { token, view }
 * ui → engine   'phone:order'  { token, id }
 * ui → engine   'phone:callback' { token, id }
 * ui → engine   'phone:close'  { token }
 *
 * Row    = { id, label, detail, state: 'ready'|'full', reason: string|null }
 * status = 'READY'|'ORDERED'|'INBOUND'|'DELIVER'|'OUTBOUND'   // VERBATIM
 * view   = 'missions'|'order'
 * ```
 *
 * ⚠ `'phone:close'` IS NOT ONLY OURS TO SEND — `Cutscene.jsx`'s lesson, and the
 * unrecoverable one. The model closes the phone from its own side when the level
 * is torn down, when the player dies with it open, or when a call arrives that
 * outranks it; a panel left on screen over a FROZEN loop with nothing left to
 * answer it is a dead game. So this listens to the channel it writes to,
 * filtered by `sentRef` so it never hears its own emit — and the close is
 * emitted exactly ONCE per token, so a double click cannot hand the model two
 * answers for one visit.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT FREEZES THE SIM, AND IT EATS THE KEYBOARD
 * ─────────────────────────────────────────────────────────────────────────────
 * The freeze goes through `freezeOwner.js` — a per-engine REFERENCE COUNT, not
 * a bare `setTimeScale(0)`. A phone call can end straight into this panel, and
 * two overlays each restoring the scale on their own cleanup is the
 * "last one to restore wins" defect `docs/shops.md` records. ⚠ The hook is keyed
 * on the BOOLEAN `open`, never on the snapshot: `'phone:state'` hands us a new
 * object after every order, and re-running would flick the scale 0 → 1 → 0 and
 * hand one real step of the sim to a world nobody is looking at.
 *
 * ⚠⚠ THE KEYBOARD IS REGISTERED ONCE, AT MOUNT, IN A `useLayoutEffect` WITH
 * DEPS `[]`, AND IT IS NEVER RE-RUN. This is REGISTRATION ORDER and it is
 * load-bearing: window capture listeners on the same node fire in the order they
 * were ADDED, not by z-index, and `PauseMenu.jsx` adds its own Escape swallower
 * when the HUD mounts. A listener added when the phone OPENS would never see
 * Escape — the pause menu would take it, open PAUSED invisibly underneath, and
 * then restore the time scale to 1 on its own cleanup. That was a LIVE bug here,
 * reproduced in the browser against the shop. React runs LAYOUT effects for a
 * commit before any PASSIVE effect of that commit, which is what lets this beat
 * a `useEffect` in a component mounted earlier in the tree. Re-registering moves
 * us to the back of the queue and restores the bug SILENTLY, which is why the
 * live handler is rebuilt into `keyRef` every render and the listener never is.
 *
 * ⚠ The handler's FIRST line returns WITHOUT touching the event when the phone
 * is closed. That listener outlives the panel and there are now four of them on
 * this node; swallowing there would eat the whole game's input. The rule is what
 * lets four coexist, not a defensive nicety.
 *
 * ⚠ Only `keydown` is swallowed, never `keyup` — swallowing keyup strands a held
 * key in `Input.downKeys` for ever. A Ctrl/Meta/Alt chord is swallowed from the
 * sim but never acted on and never cancelled, so Ctrl+R still reloads. And
 * `clearGameInput` runs on BOTH edges, or a key held when the phone opened is
 * still held when it closes and the player walks off on their own.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LAYOUT
 * ─────────────────────────────────────────────────────────────────────────────
 * `z-[65]` — above `Cutscene`'s 60 and the phone CALL's 62, below `ShopPanel`'s
 * 70. The ordering is "innermost thing the player is inside": a till is a place
 * they walked to, a phone is a thing in their pocket, so a shop covers a phone.
 * Anything added here goes in `routes/PlayRoute.jsx`'s layout block too.
 *
 * THE HANDSET: `h-[min(43rem,92vh)] w-[min(23.5rem,52vh,92vw)]`, a 9 px side
 * bezel with a 15 px band top and bottom, and a screen at `rounded-[1.65rem]`
 * inside a shell at `rounded-[2.2rem]`.
 *
 * ⚠ THE HEIGHT IS FIXED AND THE BODY SCROLLS — it is not `max-h` any more. A
 * handset whose outline changed with its contents would be a card again: the
 * missions app is long, the order app is three rows, and a shell that shrank to
 * fit the shorter one would stop reading as an object.
 *
 * ⚠ `52vh` IS IN THE WIDTH CLAMP ON PURPOSE. Without it a short window keeps
 * the full 22 rem against a squashed height and the slab goes stubby, which is
 * the one aspect ratio that reads as neither phone nor card. The radii are
 * paired — the shell's is the screen's PLUS the side bezel (26.4 + 9 ≈ 35.2) —
 * so widening one without the other leaves a crescent of bezel in each corner.
 *
 * ⚠ THE NARROW WIDTH IS A CONSTRAINT ON EVERYTHING INSIDE IT. The apps moved
 * UNDER the title, the footer stacked, and `MissionRows` is now rendering into
 * ~300 px — it truncates rather than wrapping, which is why nothing here may
 * grow a second inline control beside a row's Track button.
 *
 * Returns `null` until `'phone:open'` arrives, so a level with no phone is
 * byte-identical to a build without this file.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */

/** Keys that put the phone away. P is the key that took it out. */
const CLOSE_KEYS = new Set(['Escape', 'KeyP']);

/** Keys that act on the highlighted row. */
const ORDER_KEYS = new Set(['Enter', 'NumpadEnter', 'Space']);

/**
 * The screens, in the order the header prints them. ⚠ This array is the ONLY
 * thing in the file that knows the view names, and it is navigation order — the
 * model still owns which one is current.
 */
const VIEWS = Object.freeze([
  { id: 'missions', label: 'Missions' },
  { id: 'order', label: 'Order' },
]);

export default function PhonePanel({ engine }) {
  /**
   * THE ONLY STATE: the last snapshot the model sent. Never a derived copy of a
   * status, a row list or a view.
   * @type {[null|{token: string, view?: string, status?: string, rows?: any[], calls?: any[]}, Function]}
   */
  const [phone, setPhone] = useState(null);
  /** Keyboard highlight. Presentation only — it decides nothing about an order. */
  const [cursor, setCursor] = useState(0);
  /** Latches our close emit, so one token can only ever be answered once. */
  const sentRef = useRef('');
  const phoneRef = useRef(null);
  const cursorRef = useRef(0);
  const listRef = useRef(null);
  /** The live keydown handler. Rebuilt every render, registered exactly once. */
  const keyRef = useRef(null);

  phoneRef.current = phone;
  cursorRef.current = cursor;

  // The missions app's data. Subscribed unconditionally (hooks are not
  // conditional) and cheap when the phone is shut: `'mission:list'` fires on
  // change, and `useMissionList` owns the mount-time ask that makes it arrive at
  // all — see its header, the rule is not optional and it lives in one place.
  const list = useMissionList(engine);
  const feed = useBlipFeed(engine);
  const missions = list?.missions ?? [];
  const me = resolvePlayerMark(engine, feed.current, { x: 0, z: 0, yaw: 0, live: false });

  /**
   * Moves the highlight, keeping the ref coherent with the state. ⚠ The ref
   * matters as much as the state: an order emits into the model, which
   * republishes `'phone:state'` SYNCHRONOUSLY inside that same call, and the
   * handler reads `cursorRef` long before React has re-rendered.
   */
  const place = useCallback((i) => {
    cursorRef.current = i;
    setCursor(i);
  }, []);

  /**
   * Takes a snapshot and puts the highlight where it belongs — see `cursorFor`.
   * @param {any} snap
   * @param {any} keepId id under the cursor before this snapshot, or null.
   */
  const adopt = useCallback(
    (snap, keepId) => {
      phoneRef.current = snap;
      setPhone(snap);
      place(cursorFor(snap, keepId));
    },
    [place],
  );

  // ── subscribe ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const bus = engine?.bus;
    if (!bus?.on) return undefined;

    const offOpen = bus.on('phone:open', (p) => {
      if (!p?.token) return;
      // The model is the authority on what is open; a fresh open re-arms the
      // latch, so a second look at the phone can still be answered.
      sentRef.current = '';
      adopt(p, null);
    });

    // Republished after every order, callback and view change. Accepted ONLY for
    // the token we are showing: a state for a token we already closed must not
    // resurrect the panel.
    const offState = bus.on('phone:state', (p) => {
      if (!p?.token) return;
      const cur = phoneRef.current;
      if (!cur || cur.token !== p.token) return;
      // ⚠ HOLD THE PLAYER'S ROW ACROSS A REPUBLISH — carry the id under the
      // cursor into the new snapshot, or every order jumps the selection out
      // from under a player mid-click.
      adopt(p, rowsOf(cur)[cursorRef.current]?.id ?? null);
    });

    // ⚠ The two-way half. Ours are filtered out by `sentRef`, so this can only
    // ever fire for somebody else's close — the level going away, the player
    // dying with the phone out, a call that outranks the panel.
    const offClose = bus.on('phone:close', (p) => {
      const token = typeof p === 'string' ? p : p?.token;
      if (!token || sentRef.current === token) return;
      if (phoneRef.current?.token !== token) return;
      phoneRef.current = null;
      setPhone(null);
      place(0);
    });

    return () => {
      offOpen?.();
      offState?.();
      offClose?.();
    };
  }, [engine, adopt, place]);

  // A new engine means the old world is gone — never leave a disposed level's
  // phone on screen, and never answer its token afterwards.
  useEffect(() => {
    phoneRef.current = null;
    setPhone(null);
    setCursor(0);
    cursorRef.current = 0;
    sentRef.current = '';
  }, [engine]);

  const open = !!phone;

  // ── freeze the sim ─────────────────────────────────────────────────────────
  // Single-owner, reference counted — see `freezeOwner.js`. Keyed on the
  // BOOLEAN, never on the snapshot.
  useFreeze(engine, 'phone-panel', open);

  // ── drop held keys on both edges ───────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    clearGameInput(engine);
    return () => clearGameInput(engine);
  }, [engine, open]);

  // ── actions ────────────────────────────────────────────────────────────────
  /** Put the phone away. Answers the model exactly once for this token. */
  const close = useCallback(() => {
    const s = phoneRef.current;
    if (!s) return;
    if (sentRef.current !== s.token) {
      sentRef.current = s.token;
      engine?.bus?.emit?.('phone:close', { token: s.token });
    }
    phoneRef.current = null;
    setPhone(null);
    place(0);
  }, [engine, place]);

  /**
   * Ask for screen `id`. ⚠ Nothing is set locally — the header re-draws when
   * `'phone:state'` comes back with the new view. See the header.
   * @param {string} id
   */
  const setView = useCallback(
    (id) => {
      const s = phoneRef.current;
      if (!s || !id || viewOf(s) === id) return;
      engine?.bus?.emit?.('phone:view', { token: s.token, view: id });
    },
    [engine],
  );

  /** The other screen. Two of them, so this is a toggle and not a ladder. */
  const cycleView = useCallback(
    (step) => {
      const s = phoneRef.current;
      if (!s) return;
      const at = VIEWS.findIndex((v) => v.id === viewOf(s));
      const next = VIEWS[((at < 0 ? 0 : at) + step + VIEWS.length) % VIEWS.length];
      if (next) setView(next.id);
    },
    [setView],
  );

  /**
   * Order row `i`. ⚠ The ONLY gate is `state === 'ready'` — no arithmetic on
   * ammunition, no cooldown check, no knowledge of what the row delivers.
   * Nothing is mutated: the screen changes when `'phone:state'` arrives.
   * @param {number} i
   */
  const order = useCallback(
    (i) => {
      const s = phoneRef.current;
      const row = rowsOf(s)[i];
      if (!s || !row || row.state !== 'ready' || row.id == null) return;
      // Before the emit: the model answers synchronously, and that handler
      // carries the row under the cursor into the new snapshot.
      place(i);
      engine?.bus?.emit?.('phone:order', { token: s.token, id: row.id });
    },
    [engine, place],
  );

  /** @param {any} id */
  const callback = useCallback(
    (id) => {
      const s = phoneRef.current;
      if (!s || id == null) return;
      engine?.bus?.emit?.('phone:callback', { token: s.token, id });
    },
    [engine],
  );

  /** @param {number} step */
  const move = useCallback(
    (step) => {
      const rows = rowsOf(phoneRef.current);
      if (!rows.length) return;
      place((cursorRef.current + step + rows.length) % rows.length);
    },
    [place],
  );

  // ── keyboard ───────────────────────────────────────────────────────────────
  // ⚠ THE LIVE HANDLER LIVES IN A REF, AND THE LISTENER IS REGISTERED ONCE, AT
  // MOUNT, IN A LAYOUT EFFECT. See the header — registration ORDER, not taste.
  keyRef.current = (e) => {
    // Closed: not our keyboard. Return WITHOUT touching the event — this
    // listener outlives the panel and shares the node with three others.
    if (!phoneRef.current) return;

    // A chord belongs to the browser: swallow it so the sim never sees it, but
    // never act on it and never cancel it (Ctrl+R must still reload).
    if (e.ctrlKey || e.metaKey || e.altKey) {
      e.stopImmediatePropagation();
      return;
    }

    if (CLOSE_KEYS.has(e.code)) {
      // ⚠ Esc is SWALLOWED, never forwarded — a pause menu over this panel
      // would restore the time scale on its own cleanup. See the header.
      e.preventDefault();
      e.stopImmediatePropagation();
      close();
      return;
    }

    if (e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
      e.preventDefault();
      e.stopImmediatePropagation();
      cycleView(e.code === 'ArrowRight' ? 1 : -1);
      return;
    }

    if (e.code === 'Tab') {
      // Kept inside the panel — focus escaping to the page chrome is how a
      // player ends up typing into nothing. Tab is the SCREEN switch here (the
      // arrows already own the cursor), which also means focus never leaves.
      e.preventDefault();
      e.stopImmediatePropagation();
      cycleView(e.shiftKey ? -1 : 1);
      return;
    }

    if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
      e.preventDefault();
      e.stopImmediatePropagation();
      move(e.code === 'ArrowDown' ? 1 : -1);
      return;
    }

    // ⚠ ORDERING IS GATED ON THE VISIBLE SCREEN, and that is not a second
    // permission model — the model still decides whether an order lands. It is
    // the mis-tap rule from `docs/ui.md` applied to the keyboard: a player on
    // the missions screen pressing Enter must not spend an order they cannot
    // see. The rows exist in the snapshot on both screens; only one screen is
    // showing them.
    if (viewOf(phoneRef.current) === 'order') {
      const digit = digitOf(e.code);
      if (digit > 0) {
        e.preventDefault();
        e.stopImmediatePropagation();
        order(digit - 1);
        return;
      }
      if (ORDER_KEYS.has(e.code)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        order(cursorRef.current);
        return;
      }
    }

    // Everything else is swallowed: WASD on the phone must not survive into the
    // first step after the player puts it away.
    e.stopImmediatePropagation();
  };

  // ⚠ `useLayoutEffect`, deps `[]`, AND NEVER RE-RUN. Both halves are the fix.
  useLayoutEffect(() => {
    const onKeyDown = (e) => keyRef.current?.(e);
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  // Keep the highlighted row in view when the list is long enough to scroll.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-phone-row="${cursor}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [open, cursor]);

  if (!phone) return null;

  const view = viewOf(phone);
  const rows = rowsOf(phone);
  const calls = callsOf(phone);
  const at = rows.length ? Math.min(cursor, rows.length - 1) : -1;
  const doneCount = missions.filter((m) => m?.state === 'complete').length;

  return (
    <div
      data-hud-overlay=""
      className="hud-fade-in pointer-events-auto absolute inset-0 z-[65] flex items-center justify-center bg-void/85 backdrop-blur-[2px]"
    >
      {/* ── THE HANDSET ─────────────────────────────────────────────────────────
          The BEZEL is the dialog root: a portrait slab with a speaker slit, a
          home bar and three side keys, and the SCREEN is the only thing inside
          it. See the file header — the shape is what makes this read as the
          thing in the player's pocket rather than as one more menu. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Phone"
        className="relative flex h-[min(43rem,92vh)] w-[min(23.5rem,52vh,92vw)] flex-col rounded-[2.2rem] border border-hairline bg-void px-[9px] pb-[15px] pt-[15px] shadow-[inset_0_0_0_1px_rgba(230,233,242,0.04),0_30px_90px_rgba(0,0,0,0.75)]"
      >
        {/* Side keys. Pure silhouette, drawn OUTSIDE the shell against the
            scrim — they carry no behaviour and are never focusable. */}
        <span aria-hidden="true" className="absolute -left-[3px] top-[5.5rem] h-9 w-[3px] rounded-l-full bg-steel" />
        <span aria-hidden="true" className="absolute -left-[3px] top-[8.5rem] h-9 w-[3px] rounded-l-full bg-steel" />
        <span aria-hidden="true" className="absolute -right-[3px] top-[7rem] h-14 w-[3px] rounded-r-full bg-steel" />

        {/* Earpiece + camera, in the top bezel band. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-[5px] flex -translate-x-1/2 items-center gap-1.5"
        >
          <span className="block h-[4px] w-11 rounded-full bg-hairline" />
          <span className="block h-[5px] w-[5px] rounded-full bg-hairline" />
        </span>

        {/* Home bar, in the bottom bezel band. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[6px] left-1/2 h-[4px] w-24 -translate-x-1/2 rounded-full bg-hairline"
        />

        {/* ── THE SCREEN ─────────────────────────────────────────────────────
            Everything below here is the old card, narrowed: same header /
            body / footer, same rows, same key ladder. */}
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[1.65rem] bg-ink">
          {/* Glass sheen. `pointer-events-none`, above the content and purely
              cosmetic — it must never sit between a finger and a row. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-10 rounded-[1.65rem] bg-gradient-to-b from-hud/[0.05] to-transparent to-40%"
          />

          {/* ── the status strip ────────────────────────────────────────────
              ⚠ DECORATION, AND DELIBERATELY NUMBERLESS. It is a signal glyph
              and a battery glyph, `aria-hidden`, drawn in `currentColor` —
              never a clock, a percentage or a carrier. A readout with a NUMBER
              on it is a readout a player will trust, and this file has no data
              behind one; a review tool that lies is worse than no review tool,
              and so is a HUD. */}
          <div
            aria-hidden="true"
            className="flex shrink-0 items-center justify-between px-4 pb-[3px] pt-2.5 text-hud-faint"
          >
            <svg width="14" height="9" viewBox="0 0 14 9" fill="currentColor">
              <rect x="0" y="6" width="2.6" height="3" rx="0.6" />
              <rect x="3.8" y="4" width="2.6" height="5" rx="0.6" />
              <rect x="7.6" y="2" width="2.6" height="7" rx="0.6" />
              <rect x="11.4" y="0" width="2.6" height="9" rx="0.6" opacity="0.35" />
            </svg>
            <svg width="18" height="9" viewBox="0 0 18 9" fill="currentColor">
              <rect x="0.5" y="0.5" width="14" height="8" rx="1.8" fill="none" stroke="currentColor" />
              <rect x="2" y="2" width="9" height="5" rx="0.8" />
              <rect x="15.6" y="3" width="1.8" height="3" rx="0.9" />
            </svg>
          </div>

          {/* ── header: the device, and its two apps ─────────────────────────
              ⚠ The apps sit UNDER the title, full width, rather than beside it:
              on a 300 px screen a title and two pills on one line is how a
              header starts truncating its own navigation. */}
          <header className="shrink-0 border-b border-hairline px-4 pb-3">
            <h2 className="gta-outline font-display text-[19px] uppercase leading-none text-hud">
              Phone
            </h2>

            {/* TWO SCREENS OF A DEVICE, not tabs over one list — see the header.
                Each one only ASKS; the model answers with `'phone:state'`. */}
            <nav aria-label="Phone apps" className="mt-3 grid grid-cols-2 gap-1.5">
              {VIEWS.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  aria-current={view === v.id ? 'page' : undefined}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setView(v.id)}
                  style={{ touchAction: 'manipulation' }}
                  className={`w-full rounded-[3px] border py-2 font-display text-[12px] uppercase tracking-wide transition ${
                    view === v.id
                      ? 'border-gold/70 bg-steel text-gold'
                      : 'border-hairline bg-void/60 text-hud-dim hover:border-gold hover:text-gold'
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </nav>
          </header>

          {/* ── the body ────────────────────────────────────────────────────── */}
          <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-2.5 py-3.5">
            {view === 'order' ? (
              <>
                {/* THE STATUS, VERBATIM. One neutral treatment for every value —
                    a colour table keyed on the string would be this file forming
                    an opinion about a word the model owns, and would print the
                    sixth status in the wrong voice. */}
                <div className="mb-4 flex items-center gap-2.5 px-1">
                  <span className="font-hud text-[10px] font-bold uppercase tracking-[0.3em] text-hud-faint">
                    Status
                  </span>
                  <span
                    role="status"
                    className="rounded-[2px] border border-gold/60 bg-void/70 px-2 py-[2px] font-display text-[12px] uppercase leading-[1.5] tracking-[0.14em] text-gold"
                  >
                    {statusText(phone)}
                  </span>
                </div>

                {rows.length === 0 ? (
                  <p className="px-2 py-6 text-center font-hud text-[13px] uppercase tracking-[0.2em] text-hud-faint">
                    Nothing to order
                  </p>
                ) : (
                  <ul className="flex flex-col gap-1.5">
                    {rows.map((row, i) => (
                      <li key={rowKey(row, i)}>
                        <PhoneRow
                          row={row}
                          index={i}
                          selected={i === at}
                          onOrder={() => order(i)}
                          onHover={() => setCursor(i)}
                        />
                      </li>
                    ))}
                  </ul>
                )}

                {/* MISSED CALLS. Absent when there are none — this is not the
                    primary content of the screen, so it does not get an empty
                    state; the rows above do. */}
                {calls.length ? (
                  <section className="mt-6">
                    <h3 className="mb-2 px-1 font-hud text-[10px] font-bold uppercase tracking-[0.3em] text-hud-faint">
                      Missed calls
                    </h3>
                    <ul className="flex flex-col gap-1.5">
                      {calls.map((c, i) => (
                        <li
                          key={rowKey(c, i)}
                          className="flex items-center gap-3 rounded-[3px] border border-hairline bg-ink/70 px-3 py-2.5"
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-display text-[14px] uppercase leading-none tracking-[0.04em] text-hud">
                              {callTitle(c)}
                            </span>
                            {callDetail(c) ? (
                              <span className="mt-1 block truncate font-hud text-[12px] leading-none text-hud-dim">
                                {callDetail(c)}
                              </span>
                            ) : null}
                          </span>
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => callback(c?.id)}
                            style={{ touchAction: 'manipulation' }}
                            className="shrink-0 rounded-[3px] border border-hairline px-3 py-1.5 font-hud text-[10px] uppercase tracking-[0.2em] text-hud-dim transition hover:border-gold hover:text-gold"
                          >
                            Call back
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}
              </>
            ) : (
              <>
                <div className="mb-4 px-1 font-hud text-[11px] uppercase tracking-[0.34em] text-hud-faint">
                  {doneCount} of {missions.length} complete
                </div>
                {/* The pause menu's rows, without the pause menu's chrome — the
                    card above already owns the header, the close key and the
                    navigation. See `MissionList.jsx`'s header. */}
                <div className="flex flex-col gap-7">
                  <MissionRows missions={missions} engine={engine} me={me} />
                </div>
              </>
            )}
            </div>

          {/* ── footer ──────────────────────────────────────────────────────
              ⚠ STACKED, not a row: on a handset the key ladder and the Close
              button cannot share a line without one of them truncating, and
              the ladder is the half that must stay whole. */}
          <footer className="shrink-0 border-t border-hairline px-4 pb-3 pt-2.5">
            {/* THE KEYS, PRINTED — the binding and the pixel cannot drift
                apart. The hint INSTRUCTS (the app buttons above are what
                STATE). */}
            <p className="text-center font-hud text-[10px] uppercase leading-[1.5] tracking-[0.16em] text-hud-faint">
              {view === 'order'
                ? '↑↓ pick · Enter order · ←→ apps · Esc / P — close'
                : '←→ apps · Esc / P — close'}
            </p>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={close}
              style={{ touchAction: 'manipulation' }}
              className="mt-2 w-full rounded-[3px] border border-hairline bg-void/60 py-3 font-display text-[13px] uppercase tracking-wide text-hud transition hover:border-gold hover:text-gold"
            >
              Close
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
}

/**
 * One orderable line. ⚠ `state === 'ready'` is the ONLY thing that enables it,
 * and every other state shares ONE disabled look on purpose: telling `'full'`
 * from a future `'cooldown'` by colour would be this file forming an opinion
 * about WHY, and `reason` is the thing that says why.
 *
 * @param {{row: any, index: number, selected: boolean, onOrder: Function, onHover: Function}} props
 */
function PhoneRow({ row, index, selected, onOrder, onHover }) {
  const can = row.state === 'ready';
  const reason = typeof row.reason === 'string' && row.reason ? row.reason : '';
  const detail = typeof row.detail === 'string' && row.detail ? row.detail : '';

  return (
    <button
      type="button"
      data-phone-row={index}
      disabled={!can}
      aria-disabled={!can}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={() => onHover()}
      onClick={can ? () => onOrder() : undefined}
      style={{ touchAction: 'manipulation' }}
      className={`group flex w-full items-center gap-3 rounded-[3px] border px-3 py-2.5 text-left transition ${
        can
          ? 'cursor-pointer border-hairline bg-steel/50 hover:border-gold hover:bg-steel'
          : 'cursor-not-allowed border-hairline/50 bg-ink/60 opacity-60'
      } ${selected ? 'ring-1 ring-gold/70' : ''}`}
    >
      {/* The number key that orders it. Only the first nine rows have one. */}
      <span
        aria-hidden="true"
        className={`w-4 shrink-0 text-center font-hud text-[11px] font-bold tabular-nums ${
          can ? 'text-hud-faint group-hover:text-gold' : 'text-hud-faint/60'
        }`}
      >
        {index < 9 ? index + 1 : ''}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate font-display text-[15px] uppercase leading-none tracking-[0.04em] text-hud">
          {typeof row.label === 'string' ? row.label : String(row.id ?? '')}
        </span>
        {/* Printed VERBATIM. This file does not know what a payload is. */}
        {detail ? (
          <span className="mt-1 block truncate font-hud text-[12px] leading-none text-hud-dim">
            {detail}
          </span>
        ) : null}
        {!can && reason ? (
          <span className="mt-1 block truncate font-hud text-[11px] uppercase leading-none tracking-[0.16em] text-hud-faint">
            {reason}
          </span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * The orderable rows of a snapshot, defensively. A malformed payload draws an
 * empty screen rather than throwing inside the HUD tree — which would take the
 * whole overlay, and the freeze release with it.
 * @param {any} phone
 * @returns {any[]}
 */
function rowsOf(phone) {
  return Array.isArray(phone?.rows) ? phone.rows : [];
}

/**
 * @param {any} phone
 * @returns {any[]}
 */
function callsOf(phone) {
  return Array.isArray(phone?.calls) ? phone.calls : [];
}

/**
 * Which screen the MODEL says is up. Defaults to the missions app when the
 * payload says nothing — a phone with no view is still a phone, and the missions
 * app is the one that always has something in it.
 * @param {any} phone
 * @returns {string}
 */
function viewOf(phone) {
  const v = phone?.view;
  return VIEWS.some((x) => x.id === v) ? v : 'missions';
}

/**
 * The status word, printed exactly as it arrived. No mapping, no title-casing,
 * no fallback vocabulary of our own — an unknown status prints itself, which is
 * the same passthrough rule `ShopPanel.jsx` applies to a shop `kind`.
 * @param {any} phone
 * @returns {string}
 */
function statusText(phone) {
  const s = phone?.status;
  return typeof s === 'string' && s ? s : '—';
}

/** @param {any} c @returns {string} */
function callTitle(c) {
  const t = c?.name ?? c?.label ?? c?.caller ?? c?.id;
  return typeof t === 'string' || typeof t === 'number' ? String(t) : '';
}

/** @param {any} c @returns {string} */
function callDetail(c) {
  const d = c?.detail ?? (c?.name ? c?.caller : null);
  return typeof d === 'string' ? d : '';
}

/**
 * Where the highlight goes for a snapshot. Transplanted from
 * `ShopPanel.jsx#cursorFor` — including both of its constraints:
 *
 *  1. `keepId` WINS. On a republish after an order the player's row is still
 *     under their pointer, and re-deriving would jump the selection out from
 *     under a mid-click player. The row is followed by ID, so it survives being
 *     re-ordered; only a row that has LEFT the list gives the highlight back.
 *  2. It reads `state` and NOTHING ELSE — the pure-renderer rule. "Which row do
 *     we start on" is exactly the small convenience that grows a second
 *     permission model if it is allowed to look at anything else.
 *
 * A highlight is a PROMISE: opening the cursor on row 0 unconditionally means a
 * screen where nothing is orderable still answers Enter with a row that does
 * nothing. So: the first `'ready'` row, falling back to 0 when none is — where
 * the highlight is honestly inert.
 *
 * @param {any} phone
 * @param {any} keepId
 * @returns {number}
 */
function cursorFor(phone, keepId) {
  const rows = rowsOf(phone);
  if (!rows.length) return 0;
  if (keepId != null) {
    const held = rows.findIndex((r) => r?.id === keepId);
    if (held >= 0) return held;
  }
  const ready = rows.findIndex((r) => r?.state === 'ready');
  return ready >= 0 ? ready : 0;
}

/**
 * @param {any} row
 * @param {number} i
 * @returns {string}
 */
function rowKey(row, i) {
  return row?.id != null ? `${row.id}` : `row-${i}`;
}

/**
 * `'Digit3'`/`'Numpad3'` → 3, anything else → 0.
 * @param {string} code
 * @returns {number}
 */
function digitOf(code) {
  const m = /^(?:Digit|Numpad)([1-9])$/.exec(code);
  return m ? Number(m[1]) : 0;
}

/**
 * Drops any key the sim thinks is held. Reached through `engine.systems`, the
 * same way `ShopPanel.jsx`, `Cutscene.jsx` and `PauseMenu.jsx` do it —
 * `createGameplay` already exposes its `Input`, so this needs no new engine
 * surface.
 * @param {any} engine
 */
function clearGameInput(engine) {
  const systems = engine?.systems;
  if (!Array.isArray(systems)) return;
  for (const s of systems) {
    if (s?.name === 'gameplay') s.input?.clear?.();
  }
}
